import type { Project } from "./Company";
import type { TypeUserData } from "./TypeUsers";

export type LocalizedText = { text: string; language: string };
export type PopulatedQuizContextRef = { _id: string; [key: string]: unknown };
export type QuizContextRef<T extends object = PopulatedQuizContextRef> =
  | string
  | (Partial<T> & { _id: string });

export const QuizQuestionType = {
  SINGLE: "single",
  MULTIPLE: "multiple",
  RANGE: "range",
  TEXT: "text",
} as const;

export type QuizQuestionType =
  (typeof QuizQuestionType)[keyof typeof QuizQuestionType];

export const QuizType = {
  COMPANIES_DIAGNOSTIC: "companies-diagnostic",
  FEEDBACK: "feedback",
} as const;

export type QuizType = (typeof QuizType)[keyof typeof QuizType];

export const QuizEvaluationType = {
  SCORE: "score",
  INTERPRETATION: "interpretation",
  RESPONSE_ONLY: "response-only",
} as const;

export type QuizEvaluationType =
  (typeof QuizEvaluationType)[keyof typeof QuizEvaluationType];

export type QuizContextRefsPayload = {
  module?: QuizContextRef;
  group?: QuizContextRef;
  program?: QuizContextRef;
  educationalHub?: QuizContextRef;
  project?: QuizContextRef<Project>;
};

export type QuizAnswer = {
  _id?: string;
  text: LocalizedText[];
  isCorrect?: boolean;
  staticPoints?: number;
  /** Author configuration only; omission means false and never changes scoring. */
  riskFlag?: boolean;
};

export type QuizQuestion = {
  _id?: string;
  text: LocalizedText[];
  type: QuizQuestionType;
  points?: number;
  range?: { from: number; to: number };
  correctRange?: { from: number; to: number };
  answers: QuizAnswer[];
};

export type QuizChapter<TQuestion = QuizQuestion> = {
  _id?: string;
  title?: LocalizedText[];
  description?: LocalizedText[];
  questions: TQuestion[];
};

/** Canonical backend configuration. Do not send a flat question list. */
export type ChapterQuizConfiguration<TQuestion = QuizQuestion> = {
  chapters: QuizChapter<TQuestion>[];
  questions?: never;
  minimumPoints?: number;
  retakeAmount: number;
};

export type QuizInterpretation = {
  title: LocalizedText[];
  formula: string;
  interpretation: {
    range: { from?: number; to?: number };
    text: LocalizedText[];
  }[];
};

export type FeedbackUser = {
  _id: string;
  avatar?: string;
  email?: string;
  name?: LocalizedText[];
  surname?: LocalizedText[];
  timezone: string;
  roles?: string[];
  selectedLanguage: string;
  latinName?: string;
  latinSurname?: string;
  shortId: string;
};

export type Quiz = QuizContextRefsPayload & {
  _id: string;
  title: LocalizedText[];
  description?: LocalizedText[];
  quiz: ChapterQuizConfiguration;
  evaluationType: QuizEvaluationType;
  type?: QuizType;
  clonedFrom?: string | Quiz;
  isDeleted?: boolean;
  isConfidential?: boolean;
  isEditable?: boolean;
  isScoreShown?: boolean;
  interpretations?: QuizInterpretation[];
  isSystemTemplate?: boolean;
  feedbackSubject?: FeedbackUser;
  feedbackRaters?: FeedbackUser[];
};

/** Chapter-only saved quiz, retained as an explicit authoring alias. */
export type ChapterQuiz = Quiz;

export type QuizAttemptChoiceAnswerPayload = {
  questionId: string;
  selectedAnswerIds: string[];
  rangeValue?: never;
};

export type QuizAttemptRangeAnswerPayload = {
  questionId: string;
  rangeValue: number;
  selectedAnswerIds?: never;
};

export type QuizAttemptAnswerPayload =
  | QuizAttemptChoiceAnswerPayload
  | QuizAttemptRangeAnswerPayload;

export type QuizAttemptChoiceAnswer = {
  questionId: string;
  selectedAnswerIds: string[];
  rangeValue?: never;
};

export type QuizAttemptRangeAnswer = {
  questionId: string;
  rangeValue: number;
  selectedAnswerIds?: never;
};

export type QuizAttemptAnswer =
  | QuizAttemptChoiceAnswer
  | QuizAttemptRangeAnswer;

export type QuizAttemptInterpretationSnapshot = {
  title: LocalizedText[];
  calculatedValue: number;
  matchedRange: { from?: number; to?: number };
  resultText: LocalizedText[];
};

export type QueuedProjectQuizClone = {
  _id: string;
  isQueuedProjectQuizClone: true;
  sourceQuiz: Quiz;
  sourceQuizId: string;
  isConfidential: boolean;
  isEditable: boolean;
  isScoreShown: boolean;
};

export type ProjectQuizBucket = {
  title: LocalizedText[];
  activationTime: number;
  quizes: (Quiz | string | QueuedProjectQuizClone)[];
};

export type NewQuizAnswer = Omit<QuizAnswer, "_id">;

export type NewQuizQuestion = Omit<QuizQuestion, "_id" | "answers"> & {
  answers: NewQuizAnswer[];
};

export type NewQuizChapter = Omit<QuizChapter<NewQuizQuestion>, "_id">;

export type NewChapterQuizConfiguration = Omit<
  ChapterQuizConfiguration<NewQuizQuestion>,
  "chapters"
> & { chapters: NewQuizChapter[] };

export type NewQuiz = Omit<
  Quiz,
  | "_id"
  | "module"
  | "group"
  | "program"
  | "educationalHub"
  | "project"
  | "clonedFrom"
  | "isDeleted"
  | "type"
  | "quiz"
> & {
  type: QuizType;
  quiz: NewChapterQuizConfiguration;
  company?: string;
};

/** Chapter-only create payload; new nested entities have no client-generated IDs. */
export type NewChapterQuiz = NewQuiz;

export type TypeQuizFilters = {
  module?: string;
  group?: string;
  program?: string;
  educationalHub?: string;
  project?: string;
  type?: QuizType[];
  isSystemTemplate?: boolean;
  isForCompanies?: boolean;
  text?: string;
};

export type TypeQuizAttemptFilters = {
  module?: string;
  group?: string;
  program?: string;
  educationalHub?: string;
  project?: string;
};

export type QuizAttempt = {
  _id: string;
  user: QuizContextRef<TypeUserData>;
  module?: QuizContextRef;
  group?: QuizContextRef;
  program?: QuizContextRef;
  educationalHub?: QuizContextRef;
  project?: QuizContextRef<Project>;
  quiz: QuizContextRef<Quiz>;
  attemptNumber: number;
  answers?: QuizAttemptAnswer[];
  score?: number;
  maxScore?: number;
  isPassed?: boolean;
  interpretationSnapshots?: QuizAttemptInterpretationSnapshot[];
  submittedAt: string;
};

export type NearestProjectQuizActionableResponse = {
  status: true;
  state: "actionable";
  quiz: Quiz;
  project: QuizContextRef<Project>;
  bucketIndex: number;
  quizIndex: number;
  attempt?: QuizAttempt;
};

export type NearestProjectQuizWaitingResponse = {
  status: true;
  state: "waiting";
  project: QuizContextRef<Project>;
  bucketIndex: number;
  unlocksAt: string;
  waitingForBucketIndex?: number;
};

export type NearestProjectQuizCompletedResponse = {
  status: true;
  state: "no-required-quiz";
  project: QuizContextRef<Project>;
};

export type NearestProjectQuizErrorResponse = {
  status: false;
  state: "error";
  message: string;
};

export type NearestProjectQuizResponse =
  | NearestProjectQuizActionableResponse
  | NearestProjectQuizWaitingResponse
  | NearestProjectQuizCompletedResponse
  | NearestProjectQuizErrorResponse;

export type QuizStatisticsAnswerOption = {
  answerId?: string;
  text: LocalizedText[];
  count: number;
  percentage?: number;
  staticPoints?: number;
};

export type QuizStatisticsQuestion = {
  questionId: string;
  text: LocalizedText[];
  type: QuizQuestionType;
  answers?: QuizStatisticsAnswerOption[];
  range?: {
    min?: number;
    max?: number;
    average?: number;
    buckets?: { from: number; to: number; count: number }[];
  };
  averageScore?: number;
  maxScore?: number;
};

export type QuizStatistics = {
  quiz: QuizContextRef<Quiz>;
  attemptsCount: number;
  completedAttemptsCount?: number;
  averageScore?: number;
  maxScore?: number;
  passRate?: number;
  interpretationResults?: {
    title: LocalizedText[];
    range: { from?: number; to?: number };
    count: number;
    percentage?: number;
  }[];
  questions: QuizStatisticsQuestion[];
};

export type FeedbackResults = {
  quiz: Quiz;
  subject: FeedbackUser;
  raters: {
    user: FeedbackUser;
    status: "pending" | "submitted" | "no-longer-eligible";
    attempt?: QuizAttempt;
  }[];
};

export type FeedbackAssignment = {
  quiz: Quiz;
  subject: FeedbackUser;
  attempt?: QuizAttempt;
};
